// 精確文字匹配和高亮工具

export interface TextMatch {
  spans: HTMLElement[];
  confidence: number;
  matchType: 'exact' | 'partial' | 'fuzzy';
  matchedText: string;
}

export class TextMatcher {
  /**
   * 標準化文字：移除空白、標點符號變化等
   */
  static normalizeText(text: string): string {
    return text
      .replace(/\s+/g, '') // 移除所有空白
      .replace(/[\u200B-\u200D\uFEFF]/g, '') // 移除零寬度字符
      .replace(/[，,。.；;：:！!？?]/g, '') // 移除標點符號
      .replace(/[（）()【】[]《》<>""''`]/g, '') // 移除括號引號
      .toLowerCase()
      .trim();
  }

  /**
   * 計算字串相似度 (簡化版本)
   */
  static calculateSimilarity(str1: string, str2: string): number {
    if (str1 === str2) return 1;
    if (str1.length === 0 || str2.length === 0) return 0;

    // 簡化的相似度計算：基於共同字符數
    const chars1 = str1.split('');
    const chars2 = str2.split('');
    const set2 = new Set(chars2);

    let intersection = 0;
    const uniqueChars1 = new Set(chars1);

    uniqueChars1.forEach(char => {
      if (set2.has(char)) {
        intersection++;
      }
    });

    const union = uniqueChars1.size + set2.size - intersection;
    const similarity = union > 0 ? intersection / union : 0;

    // 考慮長度差異的懲罰
    const lengthDiff = Math.abs(str1.length - str2.length);
    const maxLength = Math.max(str1.length, str2.length);
    const lengthPenalty = maxLength > 0 ? lengthDiff / maxLength : 0;

    return Math.max(0, similarity - lengthPenalty * 0.2);
  }

  /**
   * 在文字層中尋找最佳匹配
   */
  static findBestMatch(
    textLayer: Element,
    targetText: string,
    maxSpans: number = 8
  ): TextMatch | null {
    const normalizedTarget = this.normalizeText(targetText);
    const textSpans = Array.from(textLayer.querySelectorAll('span')) as HTMLElement[];

    if (textSpans.length === 0 || normalizedTarget.length === 0) {
      return null;
    }

    let bestMatch: TextMatch | null = null;
    let bestScore = 0;

    // 1. 精確匹配 (單個 span)
    for (const span of textSpans) {
      const spanText = span.textContent || '';
      const normalizedSpan = this.normalizeText(spanText);

      if (normalizedSpan === normalizedTarget) {
        return {
          spans: [span],
          confidence: 1.0,
          matchType: 'exact',
          matchedText: spanText,
        };
      }
    }

    // 2. 連續 spans 組合匹配
    for (let i = 0; i < textSpans.length; i++) {
      for (let length = 2; length <= Math.min(maxSpans, textSpans.length - i); length++) {
        const spanGroup = textSpans.slice(i, i + length);
        const combinedText = spanGroup.map(s => s.textContent || '').join('');
        const normalizedCombined = this.normalizeText(combinedText);

        // 精確匹配
        if (normalizedCombined === normalizedTarget) {
          return {
            spans: spanGroup,
            confidence: 1.0,
            matchType: 'exact',
            matchedText: combinedText,
          };
        }

        // 包含匹配 (目標文字在組合文字中)
        if (normalizedCombined.includes(normalizedTarget) && normalizedTarget.length > 3) {
          const confidence = normalizedTarget.length / normalizedCombined.length;
          if (confidence > 0.5 && confidence > bestScore) {
            bestMatch = {
              spans: spanGroup,
              confidence,
              matchType: 'partial',
              matchedText: combinedText,
            };
            bestScore = confidence;
          }
        }

        // 相似度匹配 (模糊匹配)
        const similarity = this.calculateSimilarity(normalizedCombined, normalizedTarget);
        if (similarity > 0.8 && similarity > bestScore) {
          bestMatch = {
            spans: spanGroup,
            confidence: similarity,
            matchType: 'fuzzy',
            matchedText: combinedText,
          };
          bestScore = similarity;
        }
      }
    }

    // 3. 單詞分割匹配 (針對長文字)
    if (!bestMatch && normalizedTarget.length > 10) {
      const targetWords = targetText.split(/[\s，,。.]+/).filter(w => w.length > 1);

      for (const word of targetWords) {
        const normalizedWord = this.normalizeText(word);
        if (normalizedWord.length < 2) continue;

        for (const span of textSpans) {
          const spanText = span.textContent || '';
          const normalizedSpan = this.normalizeText(spanText);

          if (normalizedSpan.includes(normalizedWord)) {
            const confidence = normalizedWord.length / normalizedSpan.length;
            if (confidence > bestScore) {
              bestMatch = {
                spans: [span],
                confidence: confidence * 0.7, // 降低單詞匹配的信心度
                matchType: 'partial',
                matchedText: spanText,
              };
              bestScore = confidence * 0.7;
            }
          }
        }
      }
    }

    return bestMatch;
  }

  /**
   * 智能文字匹配：結合多種策略
   */
  static smartMatch(textLayer: Element, targetText: string): TextMatch | null {
    const cleanTarget = targetText.trim();

    // 處理空白或太短的文字
    if (cleanTarget.length === 0) return null;

    // 短文字使用嚴格匹配
    if (cleanTarget.length <= 3) {
      return this.findExactMatch(textLayer, cleanTarget);
    }

    // 中等長度文字使用標準匹配
    if (cleanTarget.length <= 20) {
      return this.findBestMatch(textLayer, cleanTarget, 6);
    }

    // 長文字使用更多 spans 和分詞策略
    return this.findBestMatch(textLayer, cleanTarget, 12);
  }

  /**
   * 精確匹配 (用於短文字)
   */
  private static findExactMatch(textLayer: Element, targetText: string): TextMatch | null {
    const textSpans = Array.from(textLayer.querySelectorAll('span')) as HTMLElement[];
    const normalizedTarget = this.normalizeText(targetText);

    // 單個 span 精確匹配
    for (const span of textSpans) {
      const spanText = span.textContent || '';
      const normalizedSpan = this.normalizeText(spanText);

      if (normalizedSpan === normalizedTarget || spanText.trim() === targetText.trim()) {
        return {
          spans: [span],
          confidence: 1.0,
          matchType: 'exact',
          matchedText: spanText,
        };
      }
    }

    // 雙 span 組合匹配
    for (let i = 0; i < textSpans.length - 1; i++) {
      const span1 = textSpans[i];
      const span2 = textSpans[i + 1];
      if (!span1 || !span2) continue;

      const combinedText = ((span1.textContent || '') + (span2.textContent || '')).trim();
      const normalizedCombined = this.normalizeText(combinedText);

      if (normalizedCombined === normalizedTarget || combinedText === targetText.trim()) {
        return {
          spans: [span1, span2],
          confidence: 1.0,
          matchType: 'exact',
          matchedText: combinedText,
        };
      }
    }

    return null;
  }

  /**
   * 調試信息：顯示匹配過程
   */
  static debugMatch(textLayer: Element, targetText: string): void {
    console.group(`🔍 文字匹配調試: "${targetText}"`);

    const match = this.smartMatch(textLayer, targetText);
    if (match) {
      console.log(`✅ 找到匹配:`, {
        type: match.matchType,
        confidence: `${(match.confidence * 100).toFixed(1)}%`,
        spans: match.spans.length,
        matchedText: match.matchedText,
      });
    } else {
      console.log(`❌ 未找到匹配`);

      // 顯示可用的文字 spans
      const spans = Array.from(textLayer.querySelectorAll('span'));
      console.log(`📝 頁面包含 ${spans.length} 個文字元素:`);
      spans.slice(0, 10).forEach((span, i) => {
        const text = (span as HTMLElement).textContent || '';
        console.log(`  ${i + 1}. "${text}"`);
      });
      if (spans.length > 10) {
        console.log(`  ... 還有 ${spans.length - 10} 個元素`);
      }
    }

    console.groupEnd();
  }
}
